import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { errorDetail } from '../integrations/mcpServerRegistry';

/** Mirrors services_commerce._order_views (backend). */
export interface OrderView {
    id: string;
    status: 'pending' | 'active' | 'past_due' | 'cancelled' | 'expired' | 'refunded' | 'disputed';
    livemode: boolean;
    terms: {
        label?: string;
        billing_type?: 'one_time' | 'subscription';
        unit_amount_minor?: number;
        currency?: string;
        allowance_limit?: number | null;
        allowance_period?: string;
        application_fee_bps?: number;
        grants_create_automation?: boolean;
    };
    buyer: { type: 'user' | 'tenant'; name: string | null; email: string | null; external_id: string | null };
    totals: {
        currency: string;
        gross_minor: number;
        aimdoc_fee_minor: number;
        stripe_fee_minor: number | null;
        refunded_minor: number;
        payments: number;
    };
    allowance: { limit: number; used: number; remaining: number; period: string; period_end: string | null; status: string } | null;
    transactions: {
        id: string;
        kind: string;
        status: string;
        gross_amount_minor: number;
        application_fee_minor: number;
        stripe_processing_fee_minor: number | null;
        publisher_net_minor: number | null;
        currency: string;
        stripe_object_type: string;
        stripe_object_id: string;
        provider_created_at: string | null;
    }[];
    grants?: { action: string; expires_at: string | null; revoked_at: string | null }[];
    stripe_links: { customer: string | null; subscription: string | null; payment: string | null };
    current_period_start: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    entitlement_expires_at: string | null;
    activated_at: string | null;
    cancelled_at: string | null;
    refunded_at: string | null;
    disputed_at: string | null;
    created_at: string | null;
}

export const money = (minor: number | null | undefined, currency = 'usd') =>
    minor == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(minor / 100);

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');
const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');

type DisplayStatus = OrderView['status'] | 'cancelling';

const STATUS_VARIANT: Record<DisplayStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    active: 'default',
    // Still active (access continues) but set to end at period end — shown
    // as its own state so churn-in-progress is visible before it lands.
    cancelling: 'secondary',
    pending: 'secondary',
    past_due: 'destructive',
    disputed: 'destructive',
    cancelled: 'outline',
    expired: 'outline',
    refunded: 'outline',
};

export const orderDisplayStatus = (order: Pick<OrderView, 'status' | 'cancel_at_period_end'>): DisplayStatus =>
    order.status === 'active' && order.cancel_at_period_end ? 'cancelling' : order.status;

const StatusBadge: React.FC<{ order: Pick<OrderView, 'status' | 'cancel_at_period_end'> }> = ({ order }) => {
    const status = orderDisplayStatus(order);
    return <Badge variant={STATUS_VARIANT[status]}>{status.replace('_', ' ')}</Badge>;
};

const KIND_LABEL: Record<string, string> = {
    payment: 'Payment',
    refund: 'Refund',
    application_fee_refund: 'Aimdoc fee refund',
    dispute: 'Dispute',
    dispute_reversal: 'Dispute won',
};

/**
 * Service → Orders: every acquisition of this offering through Stripe, with
 * the money split (gross / Stripe fee / Aimdoc fee / net), allowance usage,
 * and a detail modal. Refunds and disputes happen in the publisher's Stripe
 * Dashboard; this view reflects them once the Connect event lands.
 */
const ServiceOrders: React.FC<{ serviceId: string }> = ({ serviceId }) => {
    const { get, post } = useApi();
    const [orders, setOrders] = useState<OrderView[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<OrderView | null>(null);
    const [syncing, setSyncing] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const response = await get(`/services/${serviceId}/orders`);
            setOrders(response.data.orders as OrderView[]);
        } catch (err: any) {
            toast.error(errorDetail(err, 'Could not load orders.'));
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [serviceId, get]);

    useEffect(() => {
        load();
    }, [load]);

    const openDetail = async (order: OrderView) => {
        setSelected(order);
        try {
            const response = await get(`/services/${serviceId}/orders/${order.id}`);
            setSelected(response.data as OrderView);
        } catch {
            /* keep the list row */
        }
    };

    const reconcile = async (orderId: string) => {
        setSyncing(orderId);
        try {
            const response = await post(`/services/${serviceId}/orders/${orderId}/reconcile`, {});
            const updated = response.data as OrderView;
            toast.success(`Order is now ${updated.status.replace('_', ' ')}`);
            setOrders((current) => (current ? current.map((o) => (o.id === updated.id ? updated : o)) : current));
            setSelected((current) => (current && current.id === updated.id ? updated : current));
        } catch (err: any) {
            toast.error(errorDetail(err, 'Could not sync the order.'));
        } finally {
            setSyncing(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading orders…
            </div>
        );
    }

    if (!orders || orders.length === 0) {
        return (
            <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">
                No orders yet. Once the price is live, purchases made through the catalog, the SDK, or your agent show up here.
            </div>
        );
    }

    const totals = orders.reduce(
        (acc, o) => {
            acc.gross += o.totals.gross_minor;
            acc.aimdoc += o.totals.aimdoc_fee_minor;
            acc.stripe += o.totals.stripe_fee_minor ?? 0;
            acc.refunded += o.totals.refunded_minor;
            if (o.status === 'active' || o.status === 'past_due') acc.live += 1;
            return acc;
        },
        { gross: 0, aimdoc: 0, stripe: 0, refunded: 0, live: 0 },
    );
    const currency = orders[0]?.totals.currency ?? 'usd';

    return (
        <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-4">
                {[
                    ['Active orders', String(totals.live)],
                    ['Gross collected', money(totals.gross, currency)],
                    ['Fees (Stripe + Aimdoc)', `${money(totals.stripe + totals.aimdoc, currency)}`],
                    ['Your net', money(totals.gross - totals.stripe - totals.aimdoc - totals.refunded, currency)],
                ].map(([label, value]) => (
                    <div key={label} className="rounded-md border p-3">
                        <div className="text-xs text-muted-foreground">{label}</div>
                        <div className="text-lg font-medium">{value}</div>
                    </div>
                ))}
            </div>

            <div className="rounded-md border">
                <div className="flex items-center justify-between border-b px-3 py-2">
                    <span className="text-sm font-medium">Orders</span>
                    <Button variant="ghost" size="sm" onClick={load}>
                        <RefreshCw className="mr-2 h-4 w-4" /> Refresh
                    </Button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Buyer</th>
                                <th className="px-3 py-2">Package</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2 text-right">Paid</th>
                                <th className="px-3 py-2 text-right">Stripe fee</th>
                                <th className="px-3 py-2 text-right">Aimdoc fee</th>
                                <th className="px-3 py-2 text-right">Your net</th>
                                <th className="px-3 py-2">Runs</th>
                                <th className="px-3 py-2">Access until</th>
                                <th className="px-3 py-2">Created</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => {
                                const t = order.totals;
                                const net = t.gross_minor - t.aimdoc_fee_minor - (t.stripe_fee_minor ?? 0) - t.refunded_minor;
                                return (
                                    <tr
                                        key={order.id}
                                        className="cursor-pointer border-t hover:bg-muted/40"
                                        onClick={() => openDetail(order)}
                                    >
                                        <td className="px-3 py-2">
                                            <div className="font-medium">{order.buyer.name || order.buyer.email || order.buyer.external_id || '—'}</div>
                                            {order.buyer.name && order.buyer.email && (
                                                <div className="text-xs text-muted-foreground">{order.buyer.email}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">{order.terms.label ?? '—'}</td>
                                        <td className="px-3 py-2"><StatusBadge order={order} /></td>
                                        <td className="px-3 py-2 text-right">{t.payments ? money(t.gross_minor, t.currency) : '—'}</td>
                                        <td className="px-3 py-2 text-right">{t.payments ? money(t.stripe_fee_minor, t.currency) : '—'}</td>
                                        <td className="px-3 py-2 text-right">{t.payments ? money(t.aimdoc_fee_minor, t.currency) : '—'}</td>
                                        <td className="px-3 py-2 text-right font-medium">{t.payments ? money(net, t.currency) : '—'}</td>
                                        <td className="px-3 py-2">
                                            {order.allowance ? `${order.allowance.used} / ${order.allowance.limit}` : order.status === 'active' ? 'Unlimited' : '—'}
                                        </td>
                                        <td className="px-3 py-2">
                                            {order.entitlement_expires_at ? day(order.entitlement_expires_at) : order.status === 'active' ? 'No expiry' : '—'}
                                        </td>
                                        <td className="px-3 py-2">{day(order.created_at)}</td>
                                        <td className="px-3 py-2 text-right">
                                            {order.status === 'pending' && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={syncing === order.id}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        reconcile(order.id);
                                                    }}
                                                >
                                                    {syncing === order.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sync'}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
                <DialogContent className="max-w-2xl">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    Order <StatusBadge order={selected} />
                                    {!selected.livemode && <Badge variant="outline">Test mode</Badge>}
                                </DialogTitle>
                                <DialogDescription>
                                    {selected.terms.label} · {selected.buyer.name || selected.buyer.email || selected.buyer.external_id}
                                    {selected.buyer.type === 'tenant' ? ' (workspace)' : ''}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-1 text-sm">
                                    <div className="text-xs font-medium text-muted-foreground">Terms at purchase</div>
                                    <div>{selected.terms.label}</div>
                                    <div className="text-muted-foreground">
                                        Aimdoc fee {(selected.terms.application_fee_bps ?? 0) / 100}% ·{' '}
                                        {selected.terms.grants_create_automation ? 'may automate' : 'direct runs only'}
                                    </div>
                                    <div className="text-muted-foreground">Existing buyers keep these terms if you change the price.</div>
                                </div>
                                <div className="space-y-1 text-sm">
                                    <div className="text-xs font-medium text-muted-foreground">Access</div>
                                    <div>
                                        {selected.status === 'active' || selected.status === 'past_due'
                                            ? selected.entitlement_expires_at
                                                ? `Until ${when(selected.entitlement_expires_at)}`
                                                : 'No expiry'
                                            : 'Not active'}
                                    </div>
                                    {selected.current_period_end && (
                                        <div className="text-muted-foreground">
                                            Period ends {day(selected.current_period_end)}
                                            {selected.cancel_at_period_end ? ' · cancelling — access continues until then, no further charges' : ''}
                                        </div>
                                    )}
                                    {selected.allowance && (
                                        <div className="text-muted-foreground">
                                            {selected.allowance.used} of {selected.allowance.limit} runs used
                                            {selected.allowance.period === 'billing_period' ? ' this period' : ''}
                                        </div>
                                    )}
                                    {selected.grants && selected.grants.length > 0 && (
                                        <div className="text-muted-foreground">
                                            Grants: {selected.grants.map((g) => `${g.action}${g.revoked_at ? ' (revoked)' : ''}`).join(', ')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="text-xs font-medium text-muted-foreground">Money</div>
                                {selected.transactions.length === 0 ? (
                                    <div className="text-sm text-muted-foreground">No payments recorded yet.</div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead className="text-left text-xs text-muted-foreground">
                                            <tr>
                                                <th className="py-1 pr-3">When</th>
                                                <th className="py-1 pr-3">Type</th>
                                                <th className="py-1 pr-3 text-right">Gross</th>
                                                <th className="py-1 pr-3 text-right">Stripe fee</th>
                                                <th className="py-1 pr-3 text-right">Aimdoc fee</th>
                                                <th className="py-1 text-right">Your net</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selected.transactions.map((tx) => (
                                                <tr key={tx.id} className="border-t">
                                                    <td className="py-1 pr-3">{when(tx.provider_created_at)}</td>
                                                    <td className="py-1 pr-3">
                                                        {KIND_LABEL[tx.kind] ?? tx.kind}
                                                        {tx.status !== 'succeeded' && <span className="ml-1 text-xs text-muted-foreground">({tx.status})</span>}
                                                    </td>
                                                    <td className="py-1 pr-3 text-right">{money(tx.gross_amount_minor, tx.currency)}</td>
                                                    <td className="py-1 pr-3 text-right">{tx.kind === 'payment' ? money(tx.stripe_processing_fee_minor, tx.currency) : '—'}</td>
                                                    <td className="py-1 pr-3 text-right">{tx.kind === 'payment' ? money(tx.application_fee_minor, tx.currency) : '—'}</td>
                                                    <td className="py-1 text-right">{tx.kind === 'payment' ? money(tx.publisher_net_minor, tx.currency) : '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="space-y-1 text-xs text-muted-foreground">
                                <div>Created {when(selected.created_at)}{selected.activated_at ? ` · activated ${when(selected.activated_at)}` : ''}</div>
                                {selected.cancelled_at && <div>Cancelled {when(selected.cancelled_at)}</div>}
                                {selected.refunded_at && <div>Refunded {when(selected.refunded_at)}</div>}
                                {selected.disputed_at && <div>Disputed {when(selected.disputed_at)}</div>}
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {selected.stripe_links.subscription && (
                                    <Button variant="outline" size="sm" asChild>
                                        <a href={selected.stripe_links.subscription} target="_blank" rel="noreferrer">
                                            Subscription in Stripe <ExternalLink className="ml-1 h-3 w-3" />
                                        </a>
                                    </Button>
                                )}
                                {selected.stripe_links.payment && (
                                    <Button variant="outline" size="sm" asChild>
                                        <a href={selected.stripe_links.payment} target="_blank" rel="noreferrer">
                                            Payment in Stripe <ExternalLink className="ml-1 h-3 w-3" />
                                        </a>
                                    </Button>
                                )}
                                {selected.stripe_links.customer && (
                                    <Button variant="outline" size="sm" asChild>
                                        <a href={selected.stripe_links.customer} target="_blank" rel="noreferrer">
                                            Customer in Stripe <ExternalLink className="ml-1 h-3 w-3" />
                                        </a>
                                    </Button>
                                )}
                                <Button variant="ghost" size="sm" disabled={syncing === selected.id} onClick={() => reconcile(selected.id)}>
                                    <RefreshCw className={`mr-2 h-4 w-4 ${syncing === selected.id ? 'animate-spin' : ''}`} /> Sync from Stripe
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Refunds, cancellations, and disputes are handled in your Stripe Dashboard; access updates here automatically.
                            </p>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ServiceOrders;
